import React, { useCallback } from 'react';
import { useAgent } from '../../contexts/AgentContext';
import { useAgentWhatsappNumber } from '../../hooks/useAgentWhatsappNumber';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp-color.svg';
import './mobile-whatsapp-intro-modal.scss';

const SEEN_STORAGE_KEY = 'dream-mobile-whatsapp-intro-seen';

export const hasSeenMobileWhatsappIntro = (): boolean => {
  try {
    return window.localStorage.getItem(SEEN_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};

const markSeen = () => {
  try {
    window.localStorage.setItem(SEEN_STORAGE_KEY, '1');
  } catch {
    // Private-mode storage failures just mean the modal shows again next visit.
  }
};

interface MobileWhatsappIntroModalProps {
  onClose: () => void;
}

/**
 * First-visit mobile overlay explaining that the assistant works from
 * WhatsApp (Figma 4794:68072). Offers a wa.me deep link to the agent's
 * WhatsApp number and a "view your work and apps" dismissal.
 */
const MobileWhatsappIntroModal: React.FC<MobileWhatsappIntroModalProps> = ({
  onClose,
}) => {
  const { activeAgent } = useAgent();
  const { whatsappLink } = useAgentWhatsappNumber();

  const agentName = activeAgent?.nickname || activeAgent?.name || 'Kite';

  const dismiss = useCallback(() => {
    markSeen();
    onClose();
  }, [onClose]);

  const handleChatOnWhatsapp = useCallback(() => {
    markSeen();
    if (whatsappLink) {
      window.open(whatsappLink, '_blank');
    }
    onClose();
  }, [whatsappLink, onClose]);

  return (
    <div
      className='mobile-wa-intro-overlay'
      role='dialog'
      aria-modal='true'
      aria-label='Work from WhatsApp'
    >
      <div className='mobile-wa-intro-card'>
        <h1 className='mobile-wa-intro-title'>Work from WhatsApp</h1>
        <p className='mobile-wa-intro-copy'>
          Chat and work with your secretary, all from WhatsApp. Start a new
          conversation by tapping the button below.
        </p>
        <p className='mobile-wa-intro-copy'>
          To view your projects and apps, tap the button below.
        </p>
        <div className='mobile-wa-intro-actions'>
          <button
            type='button'
            className='mobile-wa-intro-btn mobile-wa-intro-btn--whatsapp'
            onClick={handleChatOnWhatsapp}
          >
            <WhatsappIcon />
            <span>Chat to {agentName} on WhatsApp</span>
          </button>
          <button
            type='button'
            className='mobile-wa-intro-btn mobile-wa-intro-btn--dark'
            onClick={dismiss}
          >
            View your work and apps
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileWhatsappIntroModal;
